import { OrgSettings } from "@/org_settings/state"

interface PageAccessUser {
  role: string
  team?: string | null
}

type PageAccessSettings = Pick<OrgSettings, "hide_accounting_from_sales">

/**
 * The pages an org can keep from its Sales team (`hide_accounting_from_sales`, Settings >
 * Organization): the sidebar's Accounting section, what those pages open (a check, a credit memo,
 * a transaction in the accounting view) and the accounting settings. Deductions stay.
 */
const ACCOUNTING_PAGE_PREFIXES = [
  "/transactions",
  "/accounting",
  "/credit-memo",
  "/file-imports",
  "/settings/accounting",
  "/settings/coding-rules",
]

/** Whether this user is one the org keeps out of the accounting pages. Admins never are. */
export function accounting_pages_hidden(user: PageAccessUser | null | undefined, settings: PageAccessSettings): boolean {
  return !!settings.hide_accounting_from_sales && user?.role === "user" && user.team === "Sales"
}

/** Whether `path` is an accounting page this user does not get. */
export function is_page_hidden(path: string, user: PageAccessUser | null | undefined, settings: PageAccessSettings): boolean {
  if (!accounting_pages_hidden(user, settings)) return false
  const page = path.split("?")[0].toLowerCase()
  return ACCOUNTING_PAGE_PREFIXES.some((prefix: string) => page === prefix || page.startsWith(`${prefix}/`))
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const rep = { role: "user", team: "Sales" }
  const on = { hide_accounting_from_sales: true }
  const off = { hide_accounting_from_sales: false }

  describe("accounting_pages_hidden", () => {
    it("changes nothing until an org turns it on", () => {
      expect(accounting_pages_hidden(rep, off)).toBe(false)
      expect(accounting_pages_hidden(rep, {})).toBe(false)
    })

    it("applies to the Sales team only", () => {
      expect(accounting_pages_hidden(rep, on)).toBe(true)
      expect(accounting_pages_hidden({ role: "user", team: "Finance" }, on)).toBe(false)
      expect(accounting_pages_hidden({ role: "user" }, on)).toBe(false)
      expect(accounting_pages_hidden(null, on)).toBe(false)
    })

    it("never applies to an admin, whatever their team", () => {
      expect(accounting_pages_hidden({ role: "org_admin", team: "Sales" }, on)).toBe(false)
      expect(accounting_pages_hidden({ role: "system_admin", team: "Sales" }, on)).toBe(false)
      expect(accounting_pages_hidden({ role: "consultant", team: "Sales" }, on)).toBe(false)
    })
  })

  describe("is_page_hidden", () => {
    it("blocks the accounting pages and what they open", () => {
      for (const path of [
        "/transactions",
        "/transactions/deductions",
        "/accounting/checks",
        "/accounting/check/3324943",
        "/accounting/check/3324943/apply?accountingCheckId=abc",
        "/accounting/transaction/abc",
        "/accounting/open-deductions",
        "/credit-memo/abc",
        "/file-imports",
        "/file-imports/abc",
        "/settings/accounting",
        "/settings/coding-rules",
      ]) {
        expect(is_page_hidden(path, rep, on)).toBe(true)
      }
    })

    it("leaves the deductions pages and everything else alone", () => {
      for (const path of [
        "/deductions",
        "/deductions/won-pending-payment",
        "/deduction/abc",
        "/dashboard",
        "/sales-forecasting",
        "/trade-promotion-management",
        "/settings/organization",
      ]) {
        expect(is_page_hidden(path, rep, on)).toBe(false)
      }
    })

    it("does not match a page that only starts with the same letters", () => {
      expect(is_page_hidden("/transactions-report", rep, on)).toBe(false)
    })

    it("blocks nothing for anyone else", () => {
      expect(is_page_hidden("/accounting/checks", rep, off)).toBe(false)
      expect(is_page_hidden("/accounting/checks", { role: "user", team: "Finance" }, on)).toBe(false)
    })
  })
}
